// @ts-nocheck
/**
 * 差し押さえシステム
 * 借金返済期限切れ時のカード差し押さえ処理
 */

import { DEBT_CONSTANTS } from './debtManager';

// ========================================
// 定数
// ========================================

export const SEIZURE_CONSTANTS = {
  // 差し押さえ時の充当率（カード基礎価格の50%が借金返済に充当）
  REPAYMENT_RATE: 0.5,
  // 質屋のマージン（残り50%）
  PAWN_MARGIN: 0.5,
  // 自動対戦での報酬
  AUTO_BATTLE_REWARD: 5000,
  // 自動対戦での報酬天引き率
  AUTO_BATTLE_DEDUCTION_RATE: 0.5,
  // 破産後の利益上限
  PROFIT_CAP: 10000,
  // タイムアウト（秒）- 選択式差し押さえの制限時間
  SELECTION_TIMEOUT: 180,
};

// レアリティ優先順位（高い順）
const RARITY_ORDER = ['GR', 'SP', 'ALT', 'SEC', 'HR', 'UR', 'SR', 'R', 'UC', 'C'];

// ========================================
// 差し押さえ対象カードのソート
// ========================================

/**
 * コレクションをレアリティ・価格順にソート（差し押さえ優先順）
 * @param {Array} collection - コレクション
 * @param {Function} getCardValue - カード基礎価格取得関数
 * @returns {Array} ソート済みコレクション
 */
export function sortForSeizure(collection, getCardValue) {
  return [...collection].sort((a, b) => {
    // レアリティ優先（高い順）
    const aRarityIndex = RARITY_ORDER.indexOf(a.rarity);
    const bRarityIndex = RARITY_ORDER.indexOf(b.rarity);
    if (aRarityIndex !== bRarityIndex) {
      return aRarityIndex - bRarityIndex;
    }
    // 同レアリティ内は価格高い順
    return getCardValue(b) - getCardValue(a);
  });
}

// ========================================
// Phase 1: カード差し押さえ
// ========================================

/**
 * 自動差し押さえ - 高レアリティ順に自動で差し押さえ
 * @param {Array} collection - コレクション
 * @param {number} debtAmount - 借金額
 * @param {Function} getCardValue - カード基礎価格取得関数
 * @returns {Object} { seizedCards, totalRepayment, remainingDebt, pawnItems }
 */
export function autoSeize(collection, debtAmount, getCardValue) {
  const sortedCollection = sortForSeizure(collection, getCardValue);
  const seizedCards = [];
  const pawnItems = [];
  let totalRepayment = 0;
  let remainingDebt = debtAmount;

  for (const card of sortedCollection) {
    if (remainingDebt <= 0) break;

    const cardValue = getCardValue(card);
    const repaymentValue = Math.floor(cardValue * SEIZURE_CONSTANTS.REPAYMENT_RATE);

    seizedCards.push({
      ...card,
      cardValue,
      repaymentValue,
    });

    // 質屋アイテムとして記録
    pawnItems.push({
      ...card,
      basePrice: cardValue,
      pawnPrice: cardValue, // 質屋では基礎価格で販売
      seizedAt: Date.now(),
    });

    totalRepayment += repaymentValue;
    remainingDebt -= repaymentValue;
  }

  return {
    seizedCards,
    totalRepayment,
    remainingDebt: Math.max(0, remainingDebt),
    pawnItems,
  };
}

/**
 * 手動差し押さえ - プレイヤーが選択したカードを差し押さえ
 * @param {Array} selectedCards - 選択されたカード
 * @param {number} debtAmount - 借金額
 * @param {Function} getCardValue - カード基礎価格取得関数
 * @returns {Object} { seizedCards, totalRepayment, remainingDebt, pawnItems, isEnough }
 */
export function manualSeize(selectedCards, debtAmount, getCardValue) {
  const seizedCards = [];
  const pawnItems = [];
  let totalRepayment = 0;

  for (const card of selectedCards) {
    const cardValue = getCardValue(card);
    const repaymentValue = Math.floor(cardValue * SEIZURE_CONSTANTS.REPAYMENT_RATE);

    seizedCards.push({
      ...card,
      cardValue,
      repaymentValue,
    });

    pawnItems.push({
      ...card,
      basePrice: cardValue,
      pawnPrice: cardValue,
      seizedAt: Date.now(),
    });

    totalRepayment += repaymentValue;
  }

  const remainingDebt = Math.max(0, debtAmount - totalRepayment);

  return {
    seizedCards,
    totalRepayment,
    remainingDebt,
    pawnItems,
    isEnough: totalRepayment >= debtAmount,
  };
}

/**
 * 差し押さえ後のコレクションを計算
 * @param {Array} collection - 元のコレクション
 * @param {Array} seizedCards - 差し押さえカード
 * @returns {Array} 差し押さえ後のコレクション
 */
export function removeSeizedCards(collection, seizedCards) {
  // 差し押さえ枚数をカウント（同じcardId+rarityの複数枚に対応）
  const seizedCounts = new Map();
  for (const card of seizedCards) {
    const key = `${card.cardId}-${card.rarity}`;
    seizedCounts.set(key, (seizedCounts.get(key) || 0) + 1);
  }

  const removedCounts = new Map();
  const newCollection = [];

  for (const card of collection) {
    const cardKey = `${card.cardId}-${card.rarity}`;
    const seizedCount = seizedCounts.get(cardKey) || 0;
    const removedCount = removedCounts.get(cardKey) || 0;

    if (seizedCount > removedCount) {
      // まだ差し押さえ枚数に達していない場合はスキップ
      removedCounts.set(cardKey, removedCount + 1);
    } else {
      newCollection.push(card);
    }
  }

  return newCollection;
}

// ========================================
// FX/ファンド強制決済（差し押さえ時）
// ========================================

/**
 * 全ファンドポジションを強制決済（差し押さえ時）
 * 損益は無視し、証拠金のみを返却
 * @param {Object} investmentData - 投資データ
 * @returns {Object} { newInvestmentData, returnedMargin, closedCount }
 */
export function clearAllFundPositions(investmentData) {
  const holdings = investmentData.holdings || [];
  if (holdings.length === 0) {
    return { newInvestmentData: investmentData, returnedMargin: 0, closedCount: 0 };
  }

  let totalReturnedMargin = 0;
  for (const holding of holdings) {
    // 証拠金のみを返却（損益は無視）
    totalReturnedMargin += holding.margin || 0;
  }

  return {
    newInvestmentData: {
      ...investmentData,
      holdings: [],
    },
    returnedMargin: totalReturnedMargin,
    closedCount: holdings.length,
  };
}

/**
 * 全FXポジションを強制決済（差し押さえ時）
 * 損益は無視し、証拠金のみを返却
 * @param {Object} investmentData - 投資データ
 * @returns {Object} { newInvestmentData, returnedMargin, closedCount }
 */
export function clearAllFXPositions(investmentData) {
  const fxPositions = investmentData.fxPositions || [];
  if (fxPositions.length === 0) {
    return { newInvestmentData: investmentData, returnedMargin: 0, closedCount: 0 };
  }

  let totalReturnedMargin = 0;
  for (const position of fxPositions) {
    // 証拠金のみを返却（損益は無視、スワップコスト分も無視）
    totalReturnedMargin += position.margin || 0;
  }

  return {
    newInvestmentData: {
      ...investmentData,
      fxPositions: [],
    },
    returnedMargin: totalReturnedMargin,
    closedCount: fxPositions.length,
  };
}

/**
 * 全投資ポジションを強制決済（差し押さえ時のワンコール）
 * @param {Object} investmentData - 投資データ
 * @returns {Object} { newInvestmentData, fundMargin, fxMargin, totalMargin }
 */
export function clearAllInvestmentPositions(investmentData) {
  let newData = { ...investmentData };

  const fundResult = clearAllFundPositions(newData);
  newData = fundResult.newInvestmentData;

  const fxResult = clearAllFXPositions(newData);
  newData = fxResult.newInvestmentData;

  return {
    newInvestmentData: newData,
    fundMargin: fundResult.returnedMargin,
    fxMargin: fxResult.returnedMargin,
    totalMargin: fundResult.returnedMargin + fxResult.returnedMargin,
    fundClosedCount: fundResult.closedCount,
    fxClosedCount: fxResult.closedCount,
  };
}

// ========================================
// Phase 2: 自動対戦ループ
// ========================================

/**
 * 1日分の自動対戦処理をシミュレート
 * @param {number} remainingDebt - 残り借金
 * @param {Array} packCards - パックから出たカード（シミュレート用）
 * @param {Function} getCardValue - カード基礎価格取得関数
 * @returns {Object} { dayReport, newRemainingDebt, earnedGold, soldCards }
 */
export function simulateAutoBattleDay(remainingDebt, packCards, getCardValue) {
  // 対戦報酬からの天引き
  const battleReward = SEIZURE_CONSTANTS.AUTO_BATTLE_REWARD;
  const battleDeduction = Math.floor(battleReward * SEIZURE_CONSTANTS.AUTO_BATTLE_DEDUCTION_RATE);

  // パックカード売却
  let packSaleTotal = 0;
  const soldCards = packCards.map(card => {
    const cardValue = getCardValue(card);
    packSaleTotal += cardValue;
    return {
      ...card,
      saleValue: cardValue,
    };
  });

  const totalRepayment = Math.min(battleDeduction + packSaleTotal, remainingDebt);
  const newRemainingDebt = remainingDebt - totalRepayment;
  const earnedGold = battleDeduction + packSaleTotal - totalRepayment;

  return {
    dayReport: {
      battleReward,
      battleDeduction,
      packSaleTotal,
      totalRepayment,
      soldCards,
    },
    newRemainingDebt: Math.max(0, newRemainingDebt),
    earnedGold: Math.max(0, earnedGold),
  };
}

/**
 * 自動対戦ループの完全シミュレーション
 * @param {number} remainingDebt - 残り借金
 * @param {Function} generatePackCards - パックカード生成関数 () => Array
 * @param {Function} getCardValue - カード基礎価格取得関数
 * @returns {Object} { days, totalEarned, highRarityCards, finalGold }
 */
export function simulateAutoBattleLoop(remainingDebt, generatePackCards, getCardValue) {
  const days = [];
  let currentDebt = remainingDebt;
  let totalEarned = 0;
  const highRarityCards = []; // UR以上のカード記録

  while (currentDebt > 0) {
    const packCards = generatePackCards();
    const result = simulateAutoBattleDay(currentDebt, packCards, getCardValue);

    days.push({
      day: days.length + 1,
      ...result.dayReport,
      remainingDebt: result.newRemainingDebt,
    });

    // 高レアリティカード記録
    for (const card of result.dayReport.soldCards) {
      if (['GR', 'SP', 'ALT', 'SEC', 'HR', 'UR'].includes(card.rarity)) {
        highRarityCards.push(card);
      }
    }

    currentDebt = result.newRemainingDebt;
    totalEarned += result.earnedGold;
  }

  // 利益上限適用
  const cappedProfit = Math.min(totalEarned, SEIZURE_CONSTANTS.PROFIT_CAP);
  const confiscated = totalEarned - cappedProfit;

  return {
    days,
    totalDays: days.length,
    totalEarned,
    highRarityCards,
    finalGold: cappedProfit,
    confiscated,
  };
}

// ========================================
// Phase 3: どん底リスタート
// ========================================

/**
 * 初期化用のスターターカードを取得
 * @returns {Array} スターターカードのカードID配列
 */
export function getStarterCardIds() {
  // ティアE初期配布カード（仮定義、実際のゲームに合わせて調整）
  return [
    // Cレアリティの基本カードを各4枚
    'C0000001', // 見習い戦士
    'C0000002', // 光の精霊
    'C0000003', // 水の精霊
    'C0000004', // 炎の精霊
    'C0000005', // 闇の精霊
    'C0000006', // 大地の精霊
  ];
}

/**
 * どん底リスタート処理
 * @param {number} earnedProfit - 自動対戦で得た利益
 * @returns {Object} { newGold, starterCardIds }
 */
export function processBankruptcy(earnedProfit) {
  const cappedGold = Math.min(earnedProfit, SEIZURE_CONSTANTS.PROFIT_CAP);

  return {
    newGold: cappedGold,
    starterCardIds: getStarterCardIds(),
  };
}

// ========================================
// 差し押さえプレビュー
// ========================================

/**
 * 差し押さえの必要カード数をプレビュー
 * @param {Array} collection - コレクション
 * @param {number} debtAmount - 借金額
 * @param {Function} getCardValue - カード基礎価格取得関数
 * @returns {Object} { requiredCards, totalValue, isEnough }
 */
export function previewSeizure(collection, debtAmount, getCardValue) {
  const sortedCollection = sortForSeizure(collection, getCardValue);
  const requiredCards = [];
  let totalValue = 0;

  for (const card of sortedCollection) {
    if (totalValue >= debtAmount) break;

    const cardValue = getCardValue(card);
    const repaymentValue = Math.floor(cardValue * SEIZURE_CONSTANTS.REPAYMENT_RATE);

    requiredCards.push({
      ...card,
      cardValue,
      repaymentValue,
    });
    totalValue += repaymentValue;
  }

  return {
    requiredCards,
    totalValue,
    isEnough: totalValue >= debtAmount,
    shortfall: Math.max(0, debtAmount - totalValue),
  };
}

/**
 * コレクション全体の差し押さえ価値を計算
 * @param {Array} collection - コレクション
 * @param {Function} getCardValue - カード基礎価格取得関数
 * @returns {number} 総差し押さえ価値
 */
export function calculateTotalSeizureValue(collection, getCardValue) {
  return collection.reduce((total, card) => {
    const cardValue = getCardValue(card);
    return total + Math.floor(cardValue * SEIZURE_CONSTANTS.REPAYMENT_RATE);
  }, 0);
}

// ========================================
// エクスポート
// ========================================

export default {
  SEIZURE_CONSTANTS,
  sortForSeizure,
  autoSeize,
  manualSeize,
  removeSeizedCards,
  clearAllFundPositions,
  clearAllFXPositions,
  clearAllInvestmentPositions,
  simulateAutoBattleDay,
  simulateAutoBattleLoop,
  getStarterCardIds,
  processBankruptcy,
  previewSeizure,
  calculateTotalSeizureValue,
};
